import React, { useState, useEffect, useMemo } from 'react'
import { Container, Row, Col, Card, Spinner, Modal, Tabs, Badge } from 'react-bootstrap'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { selectFilterTags, selectSelectedProjectId } from '../../store/slices/projectSlice'
import { useAppSelector } from '../../store/hooks'
import { useGetModelQuery } from '../../services/modelsApi'
import { useFetchAnnotatedContentQuery, useGetContentImageQuery } from '../../services/contentApi'
import styled from 'styled-components'
import { AnnotationDataBoundingBox } from '../../types/models/annotation'
import ModelDatasetViewHeader from './ModelDatasetView/ModelDatasetViewHeader'
import ContentImage from '../tools/ContentImage'
import { useDatasetStats } from '../../utils/datasetStats'
import { StyledCard } from '../common/styled'

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 1000;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.left {
    left: 20px;
  }

  &.right {
    right: 20px;
  }
`

const CloseButton = styled(NavigationButton)`
  top: 20px;
  right: 20px;
  transform: none;
  z-index: 1001;
`

const ModalContainer = styled(Modal)`
  .modal-dialog {
    max-width: 70vw;
    margin: 1.75rem auto;
  }

  .modal-content {
    background-color: transparent;
    height: 70vh;
  }

  .modal-body {
    height: 100%;
    padding: 0;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  /* Disable hover effects */
  * {
    transform: none !important;
    transition: none !important;
    cursor: default !important;
  }
`

const GalleryContainer = styled.div`
  height: calc(78vh - 64px); /* Adjust this value based on your layout's top spacing */
  min-height: 0;
  background: transparent;
  border-radius: 8px;
  position: relative;
  display: flex;
  flex-direction: column;
`

const HeaderContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: transparent;
  padding: 1rem 1rem 0 1rem;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`

const ScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(26, 26, 26, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(74, 74, 74, 0.6);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(90, 90, 90, 0.8);
  }
`

const GridContainer = styled.div<{ $imagesPerRow: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$imagesPerRow}, minmax(0, 1fr));
  gap: 1rem;
  width: 100%;
`

const ImageGridItem = styled.div`
  aspect-ratio: 1;
  width: 100%;
  min-height: 200px;
  background: #2a2a2a;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.02);
  }
`

const StyledTabs = styled(Tabs)`
  border-bottom: none;
  margin-bottom: 1.5rem;

  .nav-item {
    margin-right: 0.5rem;
  }

  .nav-link {
    background: #2a2a2a;
    border: none;
    color: #a0a0a0;
    padding: 0.75rem 1.25rem;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      background: #3a3a3a;
      color: #fff;
    }

    &.active {
      background: #4a4a4a;
      color: #fff;
      font-weight: 500;
    }
  }
`

const SummaryCard = styled(StyledCard)`
  padding: 0.75rem;

  .card-body {
    padding: 1rem;
  }

  .card-title {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }

  h6 {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
  }
`

const StatGroup = styled.div`
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const StatLabel = styled.span`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  display: block;
`

const StatValue = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
`

const SourcesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
  margin-bottom: 0.75rem;
`

interface ModelDatasetViewProps {
  modelId?: string
  projectId?: string
  onImageSelect?: (imageId: string) => void
}

const noop = () => undefined

const ModelDatasetView: React.FC<ModelDatasetViewProps> = ({
  modelId,
  projectId,
  onImageSelect,
}): JSX.Element => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: modelData } = useGetModelQuery(modelId || '', {
    skip: !modelId,
  })
  const datasetId = modelData?.datasetid

  const [showModal, setShowModal] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0)
  const [convertedBase64, setConvertedBase64] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'training' | 'validation'>('training')
  const [trainingPage, setTrainingPage] = useState(1)
  const [validationPage, setValidationPage] = useState(1)
  const [imagesPerRow, setImagesPerRow] = useState(4)
  const itemsPerPage = 100

  const selectedFilterTags = useAppSelector(selectFilterTags)

  // Get the appropriate datasource array based on the active tab
  const datasourceArray = useMemo(() => {
    if (!modelData?.parameters) return []
    return activeTab === 'training'
      ? modelData.parameters.train_metadata?.datasource || []
      : modelData.parameters.validation_metadata?.datasource || []
  }, [modelData?.parameters, activeTab])

  const currentPage = useMemo(
    () => (activeTab === 'training' ? trainingPage : validationPage),
    [activeTab, trainingPage, validationPage]
  )

  // Get all datasources (both training and validation) for stats
  const allDatasources = useMemo(() => {
    const trainingSources = modelData?.parameters?.train_metadata?.datasource || []
    const validationSources = modelData?.parameters?.validation_metadata?.datasource || []
    return [...trainingSources, ...validationSources]
  }, [modelData?.parameters])

  const datasetStatsMap = useDatasetStats(
    selectedProjectId || '',
    modelData?.datasetid || '',
    allDatasources
  )

  // Fetch both training and validation data separately to avoid refetching on tab switch
  const {
    data: trainingContentData,
    isLoading: isTrainingLoading,
    isFetching: isTrainingFetching,
    error: trainingError,
  } = useFetchAnnotatedContentQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: modelData?.datasetid || '',
      page: trainingPage - 1,
      limit: itemsPerPage,
      metadata_filter:
        modelData?.parameters?.train_metadata?.datasource &&
        modelData.parameters.train_metadata.datasource.length > 0
          ? { datasource: modelData.parameters.train_metadata.datasource }
          : undefined,
      operator: 'and',
      sort_key: 'updated_at',
      sort_val: -1,
    },
    {
      skip:
        !selectedProjectId ||
        !modelData?.datasetid ||
        !modelData?.parameters?.train_metadata?.datasource?.length,
    }
  )

  const {
    data: validationContentData,
    isLoading: isValidationLoading,
    isFetching: isValidationFetching,
    error: validationError,
  } = useFetchAnnotatedContentQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: modelData?.datasetid || '',
      page: validationPage - 1,
      limit: itemsPerPage,
      metadata_filter:
        modelData?.parameters?.validation_metadata?.datasource &&
        modelData.parameters.validation_metadata.datasource.length > 0
          ? { datasource: modelData.parameters.validation_metadata.datasource }
          : undefined,
      operator: 'and',
      sort_key: 'updated_at',
      sort_val: -1,
    },
    {
      skip:
        !selectedProjectId ||
        !modelData?.datasetid ||
        !modelData?.parameters?.validation_metadata?.datasource?.length,
    }
  )

  // Select the appropriate data based on active tab
  const contentData = activeTab === 'training' ? trainingContentData : validationContentData
  const isLoading = activeTab === 'training' ? isTrainingLoading : isValidationLoading
  const isFetching = activeTab === 'training' ? isTrainingFetching : isValidationFetching
  const error = activeTab === 'training' ? trainingError : validationError

  // Also update the stats card display
  const getSourceImagesTotal = (sources: string[] | undefined) => {
    if (!sources) return 0
    return sources.reduce((total, source) => {
      const s = datasetStatsMap[source]
      if (!s) return total
      const images = (s.total_annotated_images || 0) + (s.total_unannotated_images || 0)
      return total + images
    }, 0)
  }

  const convertBlobToBase64 = async (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64String = reader.result as string
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64Data = base64String.split(',')[1]
        resolve(base64Data)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  const handlePageChange = (page: number) => {
    if (activeTab === 'training') {
      setTrainingPage(page)
    } else {
      setValidationPage(page)
    }
  }

  const handleTabChange = (tab: 'training' | 'validation') => {
    setActiveTab(tab)
    // Reset to page 1 when switching tabs
    if (tab === 'training') {
      setTrainingPage(1)
    } else {
      setValidationPage(1)
    }
  }

  // Add cache-busting state for image refreshing
  const [imageCacheTimestamp, setImageCacheTimestamp] = React.useState<number>(Date.now())

  // Listen for annotation updates to refresh images
  React.useEffect(() => {
    const handleAnnotationUpdate = () => {
      setImageCacheTimestamp(Date.now())
    }

    window.addEventListener('annotation-updated', handleAnnotationUpdate)
    window.addEventListener('reset-image-loading', handleAnnotationUpdate)

    return () => {
      window.removeEventListener('annotation-updated', handleAnnotationUpdate)
      window.removeEventListener('reset-image-loading', handleAnnotationUpdate)
    }
  }, [])

  // Fetch the selected image data when modal is open
  const { data: selectedImageData } = useGetContentImageQuery(
    {
      ContentID: contentData?.content[selectedImageIndex]?.id || '',
      project_id: selectedProjectId || '',
      dataset_id: modelData?.datasetid || '',
      _t: imageCacheTimestamp,
    },
    {
      skip: !showModal || !contentData?.content[selectedImageIndex],
    }
  )

  useEffect(() => {
    if (!selectedImageData || !showModal) return

    const loadImage = async () => {
      try {
        const response = await fetch(selectedImageData)
        const blob = await response.blob()
        const reader = new FileReader()
        reader.onloadend = () => {
          const base64String = reader.result as string
          const base64Data = base64String.split(',')[1]
          setConvertedBase64(base64Data)
        }
        reader.readAsDataURL(blob)
      } catch (error) {
        console.error('Error loading image:', error)
      }
    }

    loadImage()
  }, [selectedImageData, showModal])

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index)
    setShowModal(true)
    onImageSelect?.(contentData?.content[index].id || '')
  }

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : prev))
    setConvertedBase64(null)
  }

  const handleNextImage = () => {
    setSelectedImageIndex((prev) =>
      prev < (contentData?.content.length || 0) - 1 ? prev + 1 : prev
    )
    setConvertedBase64(null)
  }

  // Transform bounding boxes to include required fields
  const getBoundingBoxes = (content: any): AnnotationDataBoundingBox[] => {
    if (!content.annotation || !Array.isArray(content.annotation)) {
      return []
    }

    const boxes = content.annotation.reduce(
      (boxes: AnnotationDataBoundingBox[], annotation: any) => {
        if (annotation.metadata?.bounding_boxes) {
          return [
            ...boxes,
            ...annotation.metadata.bounding_boxes.map((box: any, index: number) => {
              // Normalize coordinates to the original image dimensions
              const normalizedBox = {
                ...box,
                xmin: Math.max(0, Math.min(content.width, box.xmin)),
                xmax: Math.max(0, Math.min(content.width, box.xmax)),
                ymin: Math.max(0, Math.min(content.height, box.ymin)),
                ymax: Math.max(0, Math.min(content.height, box.ymax)),
                class_index: index,
                class_name: 'Object',
                confidence: 1.0,
              }
              return normalizedBox
            }),
          ]
        }
        return boxes
      },
      []
    )

    return boxes
  }

  if (isLoading || isFetching) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '200px' }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <div className="alert alert-danger" role="alert">
          An error occurred while loading the dataset images.
        </div>
      </Container>
    )
  }

  return (
    <>
      <Container fluid>
        <Row className="g-4">
          {/* Left Panel - Model Information */}
          <Col lg={4}>
            <Card className="h-100" style={{ background: '#1a1a1a', border: '1px solid #404040' }}>
              <Card.Header style={{ background: '#2a2a2a', borderBottom: '1px solid #404040' }}>
                <Card.Title className="text-white mb-0">Model Information</Card.Title>
              </Card.Header>
              <Card.Body className="d-flex flex-column">
                <div className="mb-4">
                  <h6 className="text-white mb-3">Model Details</h6>
                  <div className="mb-3">
                    <strong className="text-white">Name:</strong>
                    <span className="text-light ms-2" style={{ color: '#e9ecef' }}>
                      {modelData?.name || 'Unknown Model'}
                    </span>
                  </div>
                  <div className="mb-3">
                    <strong className="text-white">Status:</strong>
                    <Badge
                      bg={
                        modelData?.state === 'TRAINED'
                          ? 'success'
                          : modelData?.state === 'TRAINING'
                          ? 'warning'
                          : 'secondary'
                      }
                      className="ms-2"
                    >
                      {modelData?.state || 'Unknown'}
                    </Badge>
                  </div>
                  <div className="mb-3">
                    <strong className="text-white">Created:</strong>
                    <span className="text-light ms-2" style={{ color: '#e9ecef' }}>
                      {modelData?.created_at
                        ? new Date(modelData.created_at).toLocaleDateString()
                        : 'Unknown'}
                    </span>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="text-white mb-3">Training Dataset</h6>
                  <div
                    className="p-3"
                    style={{
                      background: '#2a2a2a',
                      borderRadius: '6px',
                      border: '1px solid #404040',
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-light" style={{ color: '#e9ecef' }}>
                        Total Images
                      </span>
                      <span className="text-white fw-bold">
                        {getSourceImagesTotal(modelData?.parameters?.train_metadata?.datasource)}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="text-light" style={{ color: '#e9ecef' }}>
                        Sources:
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-1">
                      {modelData?.parameters?.train_metadata?.datasource?.map((source, index) => (
                        <Badge key={index} bg="primary" style={{ fontSize: '0.75rem' }}>
                          {source}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="text-white mb-3">Validation Dataset</h6>
                  <div
                    className="p-3"
                    style={{
                      background: '#2a2a2a',
                      borderRadius: '6px',
                      border: '1px solid #404040',
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-light" style={{ color: '#e9ecef' }}>
                        Total Images
                      </span>
                      <span className="text-white fw-bold">
                        {getSourceImagesTotal(
                          modelData?.parameters?.validation_metadata?.datasource
                        )}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="text-light" style={{ color: '#e9ecef' }}>
                        Sources:
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-1">
                      {modelData?.parameters?.validation_metadata?.datasource?.map(
                        (source, index) => (
                          <Badge key={index} bg="info" style={{ fontSize: '0.75rem' }}>
                            {source}
                          </Badge>
                        )
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-auto">
                  <div
                    className="p-3"
                    style={{
                      background: '#2a2a2a',
                      borderRadius: '6px',
                      border: '1px solid #404040',
                    }}
                  >
                    <h6 className="text-white mb-2">Dataset Statistics</h6>
                    <div className="row g-2">
                      <div className="col-6">
                        <div className="text-center">
                          <div className="text-white fw-bold">
                            {getSourceImagesTotal(
                              modelData?.parameters?.train_metadata?.datasource
                            )}
                          </div>
                          <small className="text-light" style={{ color: '#e9ecef' }}>
                            Training
                          </small>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-center">
                          <div className="text-white fw-bold">
                            {getSourceImagesTotal(
                              modelData?.parameters?.validation_metadata?.datasource
                            )}
                          </div>
                          <small className="text-light" style={{ color: '#e9ecef' }}>
                            Validation
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Panel - Image Gallery */}
          <Col lg={8}>
            <Card className="h-100" style={{ background: '#1a1a1a', border: '1px solid #404040' }}>
              <Card.Header style={{ background: '#2a2a2a', borderBottom: '1px solid #404040' }}>
                <ModelDatasetViewHeader
                  datasetId={datasetId || ''}
                  imagesPerRow={imagesPerRow}
                  onImagesPerRowChange={setImagesPerRow}
                  currentPage={currentPage}
                  totalImages={getSourceImagesTotal(
                    activeTab === 'training'
                      ? modelData?.parameters?.train_metadata?.datasource
                      : modelData?.parameters?.validation_metadata?.datasource
                  )}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                />
              </Card.Header>
              <Card.Body className="p-0" style={{ height: 'min(600px, calc(100vh - 300px))' }}>
                {isLoading || isFetching ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  </div>
                ) : contentData?.content && contentData.content.length > 0 ? (
                  <div style={{ height: '100%', overflow: 'auto', padding: '1rem' }}>
                    <GridContainer $imagesPerRow={imagesPerRow}>
                      {contentData.content.map((image, index) => (
                        <ImageGridItem key={image.id} onClick={() => handleImageClick(index)}>
                          <ContentImage
                            content={image}
                            onImageClick={() => handleImageClick(index)}
                            selectable={false}
                          />
                        </ImageGridItem>
                      ))}
                    </GridContainer>
                  </div>
                ) : (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <div className="text-center">
                      <div className="text-muted mb-3">No images available</div>
                      <small className="text-light" style={{ color: '#e9ecef' }}>
                        This model doesn't have any training or validation images to display.
                      </small>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <ModalContainer show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Body>
          <CloseButton onClick={() => setShowModal(false)}>
            <X size={24} />
          </CloseButton>

          <NavigationButton
            className="left"
            onClick={handlePrevImage}
            disabled={selectedImageIndex === 0}
          >
            <ChevronLeft size={24} />
          </NavigationButton>

          <NavigationButton
            className="right"
            onClick={handleNextImage}
            disabled={selectedImageIndex === (contentData?.content?.length || 0) - 1}
          >
            <ChevronRight size={24} />
          </NavigationButton>

          <ImageContainer>
            {contentData?.content?.[selectedImageIndex] && convertedBase64 ? (
              <div style={{ width: '100%', height: '100%', pointerEvents: 'none' }}>
                <ContentImage
                  content={{
                    ...contentData.content[selectedImageIndex],
                    b64_image: convertedBase64,
                  }}
                  onImageClick={noop}
                  selectable={false}
                />
              </div>
            ) : (
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            )}
          </ImageContainer>
        </Modal.Body>
      </ModalContainer>
    </>
  )
}

export default ModelDatasetView
